import type { DagRowRead, ReorderLineItemRequest } from '@/types/dag'

export type RowRole = 'parent' | 'child' | 'standalone'

export function getRowRole(row: DagRowRead): RowRole {
  if (row.parent_line_item?.id) return 'child'
  if (row.cells['sku_level'] === 'Parent') return 'parent'
  return 'standalone'
}

// Rows sharing the same non-null cluster key move together as a block;
// standalone rows have no cluster key and form their own singleton block.
function clusterKey(row: DagRowRead): string | null {
  if (row.parent_line_item?.id) return row.parent_line_item.id
  if (row.cells['sku_level'] === 'Parent') return row.id
  return null
}

function blockKeyOf(row: DagRowRead): string {
  return clusterKey(row) ?? row.id
}

/**
 * Ordered ids of the full block (parent + all its children, or just itself
 * for a standalone row) containing `rowId`, in `rows`' original relative
 * order. Assumes clusters are contiguous in `rows` (true today — see
 * `_reposition_children_after_parent` on the backend).
 */
export function getBlockIds(rows: DagRowRead[], rowId: string): string[] {
  const target = rows.find(r => r.id === rowId)
  if (!target) return [rowId]
  const key = blockKeyOf(target)
  return rows.filter(r => blockKeyOf(r) === key).map(r => r.id)
}

export interface ReorderAnchor {
  rowId: string
  anchor: ReorderLineItemRequest
}

export interface ReorderPlan {
  anchors: ReorderAnchor[]
  newOrder: string[]
}

function groupIntoBlocks(
  ids: string[],
  byId: Map<string, DagRowRead>
): { key: string; ids: string[] }[] | null {
  const blocks: { key: string; ids: string[] }[] = []
  const seenKeys = new Set<string>()
  for (const id of ids) {
    const row = byId.get(id)
    if (!row) return null
    const key = blockKeyOf(row)
    const last = blocks[blocks.length - 1]
    if (last && last.key === key) {
      last.ids.push(id)
      continue
    }
    if (seenKeys.has(key)) return null // same block split by an interruption
    seenKeys.add(key)
    blocks.push({ key, ids: [id] })
  }
  return blocks
}

function replaceBlockInPlace(
  rows: DagRowRead[],
  blockIds: string[],
  newBlockOrder: string[]
): string[] {
  const ids = rows.map(r => r.id)
  const startIdx = ids.indexOf(blockIds[0])
  const result = [...ids]
  result.splice(startIdx, blockIds.length, ...newBlockOrder)
  return result
}

function computeChildReorder(
  rows: DagRowRead[],
  byId: Map<string, DagRowRead>,
  rawCandidateIds: string[],
  movedRow: DagRowRead
): ReorderPlan | null {
  const parentId = movedRow.parent_line_item!.id
  const originalBlockIds = getBlockIds(rows, movedRow.id)

  const newBlocks = groupIntoBlocks(rawCandidateIds, byId)
  if (!newBlocks) return null
  const newBlock = newBlocks.find(b => b.key === parentId)
  if (!newBlock) return null

  const sameMembers =
    newBlock.ids.length === originalBlockIds.length &&
    originalBlockIds.every(id => newBlock.ids.includes(id))
  if (!sameMembers) return null
  if (newBlock.ids[0] !== parentId) return null // parent must stay first
  if (newBlock.ids.join() === originalBlockIds.join()) return null // no-op

  const idx = newBlock.ids.indexOf(movedRow.id)
  const afterId = idx > 0 ? newBlock.ids[idx - 1] : undefined
  const beforeId = afterId ? undefined : newBlock.ids[idx + 1]
  if (!afterId && !beforeId) return null

  return {
    anchors: [
      {
        rowId: movedRow.id,
        anchor: afterId ? { after_id: afterId } : { before_id: beforeId },
      },
    ],
    newOrder: replaceBlockInPlace(rows, originalBlockIds, newBlock.ids),
  }
}

/**
 * Handles both the standalone (1-row block) and parent (whole-cluster) move
 * cases: locates the moved block's new neighbors in `rawCandidateIds`,
 * skipping over the block's own members (its former children may still sit
 * near the parent's new spot since AG Grid's managed drag only visually
 * relocates the single dragged node), and validates the block landed at a
 * cluster boundary rather than inside someone else's cluster.
 */
function computeBlockReorder(
  rows: DagRowRead[],
  byId: Map<string, DagRowRead>,
  rawCandidateIds: string[],
  movedRow: DagRowRead
): ReorderPlan | null {
  const blockIds = getBlockIds(rows, movedRow.id)
  const blockIdSet = new Set(blockIds)

  const dropIndex = rawCandidateIds.indexOf(movedRow.id)
  if (dropIndex === -1) return null

  let predecessorId: string | undefined
  for (let i = dropIndex - 1; i >= 0; i--) {
    const id = rawCandidateIds[i]
    if (!blockIdSet.has(id)) {
      predecessorId = id
      break
    }
  }
  let successorId: string | undefined
  for (let i = dropIndex + 1; i < rawCandidateIds.length; i++) {
    const id = rawCandidateIds[i]
    if (!blockIdSet.has(id)) {
      successorId = id
      break
    }
  }
  if (!predecessorId && !successorId) return null

  const predecessorKey = predecessorId
    ? blockKeyOf(byId.get(predecessorId)!)
    : null
  const successorKey = successorId ? blockKeyOf(byId.get(successorId)!) : null
  if (
    predecessorKey !== null &&
    successorKey !== null &&
    predecessorKey === successorKey
  ) {
    return null // landed inside someone else's cluster
  }

  const originalIdx = rows.findIndex(r => r.id === blockIds[0])
  const originalPrev = originalIdx > 0 ? rows[originalIdx - 1].id : undefined
  if (originalPrev === predecessorId) return null // no-op, unchanged position

  const otherIds = rawCandidateIds.filter(id => !blockIdSet.has(id))
  const insertAt = predecessorId ? otherIds.indexOf(predecessorId) + 1 : 0
  const newOrder = [...otherIds]
  newOrder.splice(insertAt, 0, ...blockIds)

  const anchors: ReorderAnchor[] = [
    {
      rowId: movedRow.id,
      anchor: predecessorId
        ? { after_id: predecessorId }
        : { before_id: successorId },
    },
  ]
  for (let i = 1; i < blockIds.length; i++) {
    anchors.push({ rowId: blockIds[i], anchor: { after_id: blockIds[i - 1] } })
  }

  return { anchors, newOrder }
}

/**
 * Validates a row drag-drop and, if valid, computes the anchor call(s) to
 * send to the backend plus the resulting full row-id order (for an
 * optimistic cache splice). Returns null when the drop is invalid or a
 * no-op — the caller should re-sync the grid to its unchanged row order.
 *
 * `rawCandidateIds` is the grid's post-drop real-row node order (option rows
 * already filtered out).
 */
export function computeReorderPlan(
  rows: DagRowRead[],
  rawCandidateIds: string[],
  movedRowId: string
): ReorderPlan | null {
  const byId = new Map(rows.map(r => [r.id, r]))
  const movedRow = byId.get(movedRowId)
  if (!movedRow) return null

  return getRowRole(movedRow) === 'child'
    ? computeChildReorder(rows, byId, rawCandidateIds, movedRow)
    : computeBlockReorder(rows, byId, rawCandidateIds, movedRow)
}
